import { useEffect, useRef, useState } from "react";
import "./RightSlideDialog.css";
import "./index.css";
import {
  appProfileLocalStorageKey,
  appSettingsLocalStorageKey,
  databaseGenders,
  databaseOrientations,
  defaultAppSettings,
  writeToGA4,
} from "../../const";
import { XStack, YStack } from "tamagui";
import { genders, orientations } from "@shane-collective/design-system";
import { synchronizeDicts } from "../../functions/Functions";
import { Profile } from "../../types/types";
import { SettingsDialog } from "../webSettingsDialog";
import { useAppState } from "../../core/appState/UseAppStateZustand";
import { useShallow } from "zustand/shallow";

interface Props {
  // isOpen: boolean;
  onClose: () => void;
  onProfileChange: (newProfile: Profile) => void;
  isSmallScreen?: boolean;
}

export const AppSettingsDialog: React.FC<Props> = ({
  // isOpen,
  onClose,
  onProfileChange,
  isSmallScreen,
}) => {
  const isSettingsDialogOpen = useAppState(useShallow((s) => s.isSettingsDialogOpen));
  const dialogRef = useRef<HTMLElement | null>(null);
  const overlayRef = useRef<HTMLElement | null>(null);
  const [params, setParams] = useState<any>(null);
  const [lastSavedParams, setLastSavedParams] = useState<any>(null);
  const paramsRef = useRef<any>(params);

  useEffect(() => {
    paramsRef.current = params;
  }, [params]);

  useEffect(() => {
    // Prevent body scroll when the dialog is open
    if (isSettingsDialogOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isSettingsDialogOpen]);

  //SECTION - get settings from local storage
  useEffect(() => {
    let tempParams = {};
    // get settings from local storage
    const settingsString = localStorage.getItem(appSettingsLocalStorageKey);

    if (settingsString && settingsString !== "{}") {
      tempParams = JSON.parse(settingsString);

      tempParams = synchronizeDicts(tempParams, defaultAppSettings);

      localStorage.setItem(
        appSettingsLocalStorageKey,
        JSON.stringify(tempParams)
      );
    } else {
      //first run, set defaults and write to storage
      localStorage.setItem(
        appSettingsLocalStorageKey,
        JSON.stringify(defaultAppSettings)
      );
      tempParams = { ...defaultAppSettings };
      // setSettings(defaultAppSettings);
    }

    setParams({ ...tempParams });
    setLastSavedParams({ ...tempParams });
  }, []);

  useEffect(() => {
    const updateProfileFromStorage = (event: any) => {
      if (event.key === appProfileLocalStorageKey) {
        const profileString = localStorage.getItem(appProfileLocalStorageKey);
        if (!profileString) return;
        let tempParams = { ...paramsRef.current, ...JSON.parse(profileString) };

        if (tempParams?.gender) {
          tempParams.gender = genders.find(
            (g) => g.label === tempParams.gender
          );
        }

        if (tempParams?.additionalGender) {
          tempParams.additionalGender = genders.find(
            (g) => g.label === tempParams.additionalGender
          );
        }

        if (tempParams?.orientation) {
          tempParams.orientation = orientations.find(
            (g) => g.label === tempParams.orientation
          );
        }

        setParams({ ...tempParams });

        window.removeEventListener("storage", updateProfileFromStorage);
      }
    };
    window.addEventListener("storage", updateProfileFromStorage);
  }, []);
  //!SECTION

  //SECTION - save changes
  const onSubmit = (newSettings: any) => {
    const newMapSettings = {
      "Show ratings on map": newSettings["Show ratings on map"],
    };
    const newProfile: Profile = {
      gender: newSettings?.gender?.label,
      additionalGender: newSettings?.additionalGender?.label,
      orientation: newSettings?.orientation?.label,
      rememberProfile: newSettings?.rememberProfile ?? false,
      genderId:
        databaseGenders.find((g) => g.name === newSettings?.gender?.label)
          ?.id ?? null,
      additionalGenderId:
        databaseGenders.find(
          (g) => g.name === newSettings?.additionalGender?.label
        )?.id ?? null,
      orientationId:
        databaseOrientations.find(
          (g) => g.name === newSettings?.orientation?.label
        )?.id ?? null,
    };

    // persist profile if requested
    if (newSettings?.rememberProfile) {
      localStorage.setItem(
        appProfileLocalStorageKey,
        JSON.stringify(newProfile)
      );
    }

    // only want to reload the page if the page settings have changed.
    // If only profile settings have changed, set values and close
    const settingsString = localStorage.getItem(appSettingsLocalStorageKey);

    if (settingsString !== JSON.stringify(newMapSettings)) {
      // persist settings to local storage
      localStorage.setItem(
        appSettingsLocalStorageKey,
        JSON.stringify(newMapSettings)
      );

      setTimeout(() => {
        // force reload page
        window.location.reload();
      }, 200);
    } else {
      let tempParams = { ...newSettings, ...newProfile };

      // set params
      if (newProfile?.gender) {
        tempParams.gender = genders.find((g) => g.label === newProfile.gender);
      }

      if (newProfile?.additionalGender) {
        tempParams.additionalGender = genders.find(
          (g) => g.label === newProfile.additionalGender
        );
      }

      if (newProfile?.orientation) {
        tempParams.orientation = orientations.find(
          (g) => g.label === newProfile.orientation
        );
      }

      setParams({ ...tempParams });
      setLastSavedParams(JSON.parse(JSON.stringify(tempParams)));
      const { isRunningInWebView } = useAppState.getState();

      writeToGA4(
        isRunningInWebView ? "mobile_app" : "web_app",
        "settings_update"
      );

      onProfileChange(newProfile);

      onClose();
    }
  };
  //!SECTION

  return (
    <YStack
      ref={overlayRef}
      className={
        isSmallScreen
          ? `dialog-overlay-settings-center ${isSettingsDialogOpen ? "open" : ""}`
          : `dialog-overlay-settings ${isSettingsDialogOpen ? "open" : ""}`
      }
      id={"settingsDialogWrapper"}
      style={{ position: "fixed" }}
      onPress={(e: any) => {
        if (isSettingsDialogOpen) {
          if (
            dialogRef.current &&
            overlayRef.current &&
            //@ts-ignore
            !dialogRef.current.contains(e.target as Node) &&
            //@ts-ignore
            overlayRef.current.contains(e.target as Node)
          ) {
            setParams({});
            setParams({ ...lastSavedParams });
            //close dialog
            onClose();
          }
        }
      }}
    >
      {/* @ts-ignore */}
      <XStack
        className={"dialog-content-settings" + (isSmallScreen ? "-center" : "")}
        ref={dialogRef}
        height={isSmallScreen ? "28%" : "220px"}
        overflow="visible"
      >
        {paramsRef?.current && (
          <SettingsDialog
            settings={paramsRef.current}
            onSubmit={onSubmit}
            onClose={() => onClose()}
          />
        )}
      </XStack>
    </YStack>
  );
};
